/**
 * The form an invited colleague meets on their first sign-in, before anything else.
 *
 * WHY THIS BLOCKS RATHER THAN NUDGES
 *
 * A person with a login but no roster row is in a state the rest of the app has no
 * good way to draw. They cannot be made DRI of anything, they do not appear on the
 * Team page, and the schedule they are looking at is one they are absent from. A
 * dismissible banner would leave them in that state indefinitely, and the roster
 * would drift into "everyone except the people who clicked the X" - which is worse
 * than no roster, because it looks complete.
 *
 * It is a real gate, not a security one. Every route stays open server-side; skipping
 * this screen would grant nothing, and `onboarded` is explicitly documented as a
 * routing hint that FAILS OPEN. If /api/me cannot reach DynamoDB it says `true`, and
 * an older backend that never heard of the field leaves it undefined - both mean the
 * app is used as normal. The failure this avoids is locking the whole team out of a
 * working roadmap over a roster lookup, which is a far likelier outcome than somebody
 * wanting to dodge a form that takes a minute.
 *
 * There is no Cancel. The only way past is to fill it in or sign out, and sign-out is
 * offered explicitly because "I am not who this account says I am" has to have an
 * answer that is not "close the tab".
 *
 * WHY IT ASKS FOR LESS THAN THE TEAM PAGE
 *
 * An email, a name and a role. No specialisations, and therefore no star scale to read
 * before the first click - `specialisations={false}` on the editor drops the picker and
 * its legend together. The gate is load-bearing precisely because it blocks, which
 * means every field on it is a field somebody must pass to get into the app at all,
 * and eleven rated skills at that moment is how a roster fills up with rows that were
 * clicked through rather than answered. They are a PATCH away on the same form from the
 * Team page, which the footer already points at.
 *
 * A consequence worth naming: the skill vocabulary is not fetched here at all, so this
 * screen no longer waits on GET /skills to render.
 */

import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { describeError, getRoles } from '../services/api';
import { palette, radius } from '../styles/theme';
import { ErrorText, Panel, SecondaryButton } from '../styles/ui';
import type { RoleInfo } from '../types';
import PersonEditor from './PersonEditor';

const Wrap = styled(Panel)`
  max-width: 900px;
  margin: 32px auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  color: ${palette.deepMagenta};
`;

const Lede = styled.p`
  margin: 0;
  color: ${palette.inkSoft};
  font-size: 14px;
  line-height: 1.55;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-top: 4px;
  border-top: 1px solid rgba(224, 33, 138, 0.15);
`;

const Aside = styled.span`
  font-size: 12px;
  color: ${palette.inkSoft};
`;

const Loading = styled.p`
  margin: 0;
  color: ${palette.inkSoft};
  border-radius: ${radius.lg};
`;

interface OnboardingProps {
  /**
   * The signed-in address, from /api/me.
   *
   * Passed down to PersonEditor as `lockedEmail`, so the field is filled and fixed:
   * this form can only ever create the caller's own row. Somebody typing a colleague's
   * address here would be refused by the API anyway (non-admins may only add
   * themselves), but being refused after filling the form in is a poor way to find
   * that out.
   */
  email: string;
  /** Re-reads /api/me so the app unblocks once the row exists. */
  onDone: () => void;
  signOut?: () => void;
}

export default function Onboarding({ email, onDone, signOut }: OnboardingProps) {
  const [roles, setRoles] = useState<RoleInfo[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const load = useCallback(async () => {
    try {
      // Roles only. The skill vocabulary used to be fetched alongside it and is not
      // needed now that this screen does not ask about specialisations - one fewer
      // request standing between a sign-in and a form.
      setRoles(await getRoles());
      setReady(true);
    } catch (err) {
      setError(describeError(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Wrap>
      <Title>Welcome — tell us what you do</Title>
      <Lede>
        You are signed in as <strong>{email}</strong>, but you are not on the team list yet. This
        is what puts you there, so work can be assigned to you and your name shows up on the
        schedule alongside everybody else&rsquo;s.
      </Lede>

      {error ? <ErrorText role="alert">{error}</ErrorText> : null}

      {ready ? (
        <PersonEditor
          person={null}
          // Nothing renders the skill list here, so nothing needs it. Passing [] rather
          // than fetching it keeps the empty picker impossible instead of merely unseen.
          skills={[]}
          roles={roles}
          admin={false}
          specialisations={false}
          lockedEmail={email}
          onSaved={onDone}
        />
      ) : error ? null : (
        <Loading>Loading the form…</Loading>
      )}

      <Footer>
        {signOut ? (
          <SecondaryButton type="button" onClick={signOut}>
            Sign out
          </SecondaryButton>
        ) : null}
        {/* Names the Team page as the place the rest of the form lives, because this
            screen no longer asks for specialisations and somebody who wants to say
            what they are good at needs to be told where. */}
        <Aside>
          You can change any of this later from the Team page, and add what you specialise in
          there.
        </Aside>
      </Footer>
    </Wrap>
  );
}
